/**
 * LivePreviewPanel.jsx
 * Desktop: sticky right-column panel with glassmorphism.
 * Mobile: fixed bottom bar with rate + payment; tap opens MUI Drawer.
 */
import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  Drawer,
  Link,
  useMediaQuery,
  useTheme,
  Divider,
  IconButton,
} from '@mui/material';
import {
  Close as CloseIcon,
  CheckCircleOutline as CheckIcon,
  Update as UpdateIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { MORTGAGE_RATES } from '../../../data/mortgageRates';
import { useAnimatedNumber } from './useAnimatedNumber';

const AnimatedRate = ({ value }) => {
  const animated = useAnimatedNumber(value, 400);
  return (
    <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
      <Typography
        component="span"
        sx={{
          fontSize: { xs: '52px', md: '64px' },
          fontWeight: 800,
          color: '#0F1E3A',
          lineHeight: 1,
          fontFeatureSettings: "'tnum'",
          letterSpacing: '-0.04em',
        }}
      >
        {animated.toFixed(2)}
      </Typography>
      <Typography
        component="span"
        sx={{
          fontSize: { xs: '28px', md: '36px' },
          fontWeight: 700,
          color: '#0F1E3A',
          lineHeight: 1,
          ml: 0.25,
        }}
      >
        %
      </Typography>
    </Box>
  );
};

const AnimatedPayment = ({ value }) => {
  const animated = useAnimatedNumber(value, 400);
  return (
    <Typography
      sx={{
        fontSize: { xs: '28px', md: '34px' },
        fontWeight: 800,
        color: '#0F1E3A',
        lineHeight: 1,
        fontFeatureSettings: "'tnum'",
        letterSpacing: '-0.02em',
      }}
    >
      ${Math.round(animated).toLocaleString('en-CA')}
    </Typography>
  );
};

const TrustBadge = ({ icon, label }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
    {icon}
    <Typography sx={{ fontSize: '12px', color: '#5F6B7A', fontWeight: 500 }}>
      {label}
    </Typography>
  </Box>
);

const PreviewContent = ({ preview, onSeeResults, compact = false }) => {
  const { t } = useTranslation();

  if (!preview) return null;

  const { bestRate, bestBank, payment, insured, cmhcRate, amortization = 25, confidence } = preview;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: compact ? 1.5 : 2 }}>
      {/* Best rate */}
      <Box>
        <Typography
          sx={{
            fontSize: '12px', fontWeight: 600, color: '#5F6B7A',
            textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.5,
          }}
        >
          {t('mortgageEngine.preview.bestRate')}
        </Typography>
        <AnimatedRate value={bestRate} />
        {bestBank && (
          <Typography sx={{ fontSize: '12px', color: '#5F6B7A', mt: 0.25 }}>
            {bestBank}
          </Typography>
        )}
        <Typography sx={{ fontSize: '12px', color: '#5F6B7A', fontStyle: 'italic', mt: 0.5 }}>
          {t('mortgageEngine.preview.profileInsight')}
        </Typography>
        {(() => {
          const brokerRate = MORTGAGE_RATES.quebec.bestRates.find((r) => r.term === '5 años fijo')?.rate;
          return brokerRate && bestRate > brokerRate ? (
            <Typography sx={{ fontSize: '12px', color: '#5F6B7A', mt: 1, lineHeight: 1.5 }}>
              {t('mortgageEngine.preview.bestBankDirect')}{' '}
              <Link
                href="#tasas-quebec"
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById('tasas-quebec')?.scrollIntoView({ behavior: 'smooth' });
                }}
                sx={{ color: '#1E3A5F', fontWeight: 600, textDecoration: 'underline', cursor: 'pointer' }}
              >
                {t('mortgageEngine.preview.brokersFrom', { rate: brokerRate })}
              </Link>
            </Typography>
          ) : null;
        })()}
      </Box>

      <Divider sx={{ borderColor: 'rgba(230,235,243,0.5)' }} />

      {/* Estimated payment */}
      <Box>
        <Typography
          sx={{
            fontSize: '12px', fontWeight: 600, color: '#5F6B7A',
            textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.5,
          }}
        >
          {t('mortgageEngine.preview.estPayment')}
        </Typography>
        <AnimatedPayment value={payment} />
      </Box>

      <Divider sx={{ borderColor: 'rgba(230,235,243,0.5)' }} />

      {/* CMHC */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography sx={{ fontSize: '13px', color: '#5F6B7A', fontWeight: 500 }}>
          {t('mortgageEngine.preview.cmhcLabel')}:
        </Typography>
        <Chip
          size="small"
          label={
            insured
              ? t('mortgageEngine.preview.cmhcYes', { rate: cmhcRate.toFixed(1) })
              : t('mortgageEngine.preview.cmhcNo')
          }
          sx={{
            height: 22, fontSize: '12px', fontWeight: 600,
            bgcolor: insured ? '#E8F5E9' : '#F7F9FC',
            color: insured ? '#2E7D32' : '#5F6B7A',
            border: 'none',
          }}
        />
      </Box>

      {/* Amortization */}
      <Typography sx={{ fontSize: '12px', color: '#5F6B7A' }}>
        {t('mortgageEngine.preview.amortization', { years: amortization })}
      </Typography>

      {/* Confidence meter */}
      {confidence && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
            <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#5F6B7A' }}>
              {t('mortgageEngine.preview.confidence')}
            </Typography>
            <Typography sx={{ fontSize: '12px', fontWeight: 600, color: confidence === 'high' ? '#2E7D32' : confidence === 'medium' ? '#C68A00' : '#C62828' }}>
              {t(`mortgageEngine.preview.confidence_${confidence}`)}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {['low', 'medium', 'high'].map((level) => (
              <Box
                key={level}
                sx={{
                  flex: 1, height: 4, borderRadius: 2,
                  bgcolor: (
                    (level === 'low' && (confidence === 'low' || confidence === 'medium' || confidence === 'high')) ||
                    (level === 'medium' && (confidence === 'medium' || confidence === 'high')) ||
                    (level === 'high' && confidence === 'high')
                  )
                    ? confidence === 'high' ? '#2E7D32' : confidence === 'medium' ? '#F9A825' : '#C62828'
                    : '#E6E8EC',
                  transition: 'background-color 0.3s ease',
                }}
              />
            ))}
          </Box>
        </Box>
      )}

      {/* CTA */}
      <Button
        variant="contained"
        fullWidth
        onClick={onSeeResults}
        sx={{
          bgcolor: '#1E3A5F', borderRadius: '10px', textTransform: 'none',
          fontWeight: 700, py: 1.25, fontSize: '15px', mt: compact ? 0 : 0.5,
          boxShadow: '0 2px 8px rgba(30,58,95,0.20)',
          transition: 'all 0.2s ease',
          '&:hover': { bgcolor: '#1A3152', transform: 'translateY(-2px)', boxShadow: '0 4px 14px rgba(30,58,95,0.30)' },
        }}
      >
        {`${t('mortgageEngine.preview.seeResults')} →`}
      </Button>

      {/* Trust badge */}
      <TrustBadge
        icon={<CheckIcon sx={{ fontSize: 14, color: '#2E7D32' }} />}
        label={t('mortgageEngine.trust.updatedToday')}
      />
    </Box>
  );
};

/* ── Desktop Panel (sticky right column) ── */
const DesktopPanel = ({ preview, onSeeResults }) => (
  <Box
    sx={{
      position: 'sticky',
      top: 80,
      p: 4,
      borderRadius: '20px',
      bgcolor: 'rgba(248, 250, 253, 0.9)',
      backdropFilter: 'blur(16px)',
      WebkitBackdropFilter: 'blur(16px)',
      border: 'none',
      boxShadow: '0 12px 40px rgba(15,30,58,0.08), 0 2px 6px rgba(0,0,0,0.03)',
    }}
  >
    <PreviewContent preview={preview} onSeeResults={onSeeResults} />
  </Box>
);

/* ── Mobile Bottom Bar + Drawer ── */
const MobileBar = ({ preview, onSeeResults }) => {
  const { t } = useTranslation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  if (!preview) return null;

  return (
    <>
      {/* Fixed bottom bar */}
      <Box
        onClick={() => setDrawerOpen(true)}
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          height: 64,
          bgcolor: '#FFFFFF',
          borderTop: '1px solid #EEF0F3',
          boxShadow: '0 -2px 12px rgba(0,0,0,0.06)',
          zIndex: 1100,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          cursor: 'pointer',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5 }}>
          <Typography
            sx={{
              fontSize: '20px', fontWeight: 800, color: '#1E3A5F',
              fontFeatureSettings: "'tnum'",
            }}
          >
            {preview.bestRate.toFixed(2)}%
          </Typography>
          <Typography sx={{ fontSize: '14px', color: '#5F6B7A', fontFeatureSettings: "'tnum'" }}>
            ${Math.round(preview.payment).toLocaleString('en-CA')}/mo
          </Typography>
        </Box>
        <Button
          variant="contained"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onSeeResults();
          }}
          sx={{
            bgcolor: '#1E3A5F', borderRadius: '8px', textTransform: 'none',
            fontWeight: 600, fontSize: '13px', px: 2,
            '&:hover': { bgcolor: '#1A3152' },
          }}
        >
          {t('mortgageEngine.preview.seeResults')}
        </Button>
      </Box>

      {/* Drawer with full preview */}
      <Drawer
        anchor="bottom"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            borderTopLeftRadius: '20px',
            borderTopRightRadius: '20px',
            p: 3,
            maxHeight: '70vh',
          },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          <IconButton size="small" onClick={() => setDrawerOpen(false)}>
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>
        <PreviewContent preview={preview} onSeeResults={() => { setDrawerOpen(false); onSeeResults(); }} compact />
      </Drawer>
    </>
  );
};

const LivePreviewPanel = ({ preview, onSeeResults }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  if (!preview) return null;

  if (isMobile) {
    return <MobileBar preview={preview} onSeeResults={onSeeResults} />;
  }

  return <DesktopPanel preview={preview} onSeeResults={onSeeResults} />;
};

export default LivePreviewPanel;
